import { useState } from "react";

interface Lead {
  status: string;
}

interface LeadsFunnelChartProps {
  leads: Lead[];
}

type ViewMode = "funnel" | "trend" | "pipeline";

const TREND_COLORS: Record<string, string> = {
  new: "#00D4FF",
  contacted: "#A78BFA",
  proposal: "#F59E0B",
  won: "#00FF88",
  lost: "#FF4D4D",
};

function FunnelView({ leads }: LeadsFunnelChartProps) {
  const stages = [
    { id: "new", label: "New", color: "#00D4FF", icon: "ri-user-add-line" },
    { id: "contacted", label: "Contacted", color: "#A78BFA", icon: "ri-mail-send-line" },
    { id: "proposal", label: "Proposal", color: "#F59E0B", icon: "ri-file-text-line" },
    { id: "won", label: "Won", color: "#00FF88", icon: "ri-trophy-line" },
    { id: "lost", label: "Lost", color: "#FF4D4D", icon: "ri-close-circle-line" },
  ];

  const counts = stages.reduce((acc, stage) => {
    acc[stage.id] = leads.filter(l => l.status === stage.id).length;
    return acc;
  }, {} as Record<string, number>);

  const mainStages = stages.slice(0, -1);
  const lostStage = stages[4];
  const maxCount = Math.max(...mainStages.map(s => counts[s.id]));
  const totalLeads = mainStages.reduce((a, s) => a + counts[s.id], 0);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {mainStages.map((stage, idx) => {
          const widthPct = (counts[stage.id] / maxCount) * 100;
          return (
            <div key={stage.id}>
              {idx > 0 && (
                <div className="flex items-center gap-2 py-1 pl-4">
                  <div className="flex-1 h-px bg-dark-border" />
                  <span className="text-xs text-silver-dim">
                    {mainStages[idx - 1].label} → {stage.label}: {((counts[stage.id] / counts[mainStages[idx - 1].id]) * 100).toFixed(0)}%
                  </span>
                </div>
              )}
              <div className={`w-full h-14 bg-dark-border/20 rounded-xl overflow-hidden relative p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg border`} style={{ backgroundColor: `${stage.color}15`, borderColor: `${stage.color}30`, color: stage.color }}>
                    <i className={`${stage.icon} text-sm`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">{stage.label}</p>
                    <p className="text-silver-dim text-xs">{counts[stage.id]} leads</p>
                  </div>
                </div>
                <p className={`text-lg font-black`} style={{ color: stage.color }}>{counts[stage.id]}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-dark-border">
        <div className="w-full h-12 bg-dark-border/20 rounded-xl overflow-hidden relative p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg border`} style={{ backgroundColor: `${lostStage.color}15`, borderColor: `${lostStage.color}30`, color: lostStage.color }}>
              <i className={`${lostStage.icon} text-xs`} />
            </div>
            <p className="text-silver text-sm font-semibold">{lostStage.label}</p>
          </div>
          <p className="text-sm font-black" style={{ color: lostStage.color }}>{counts['lost']}</p>
        </div>
      </div>
    </div>
  );
}

export default function LeadsFunnelChart({ leads }: LeadsFunnelChartProps) {
  const [view, setView] = useState<ViewMode>("funnel");

  const viewOptions: { id: ViewMode; label: string; icon: string }[] = [
    { id: "funnel", label: "Funnel", icon: "ri-filter-line" },
    { id: "trend", label: "Volume", icon: "ri-bar-chart-grouped-line" },
    { id: "pipeline", label: "Pipeline $", icon: "ri-line-chart-line" },
  ];

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-dark-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyber-green/10 border border-cyber-green/20 text-cyber-green">
            <i className="ri-filter-3-line text-sm" />
          </div>
          <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Leads Conversion Funnel
          </h3>
        </div>
        <div className="flex items-center bg-dark-panel border border-dark-border rounded-lg p-1">
          {viewOptions.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                view === v.id
                  ? "bg-dark-card text-white border border-dark-border"
                  : "text-silver-dim hover:text-white"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <i className={`${v.icon} text-xs`} />
                {v.label}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-5">
        {view === "funnel" && <FunnelView leads={leads} />}
        {view === "trend" && <div className="text-silver-dim text-sm text-center py-8">Trend view coming soon</div>}
        {view === "pipeline" && <div className="text-silver-dim text-sm text-center py-8">Pipeline view coming soon</div>}
      </div>
    </div>
  );
}
